import { Button, Checkbox, Flex, Heading, Icon, IconButton, Select, Spinner, Stack, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr, useToast } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { BiCheck, BiFile, BiTransfer } from "react-icons/bi";
import { FaArrowLeft, FaArrowRight, FaColumns, FaDownload, FaEdit, FaFileInvoiceDollar, FaFilter, FaPlus, FaSync, FaTrash } from "react-icons/fa";
import { useSelector } from "react-redux";
import { GET, PUT } from "../../utils/ApiRequestProvider";
import { ProcessingText } from "../../components/misc/Processing";
import TabButton from "../../components/Buttons/TabButton";
import AddDocument from "../../components/Modals/AddDocument";
import EditModal, { EditModalProps } from "../SalesInvoices/EditModal";

export interface PaginationType {
    currentPage: number;
    totalPages: number;
    totalRecords: number;
}

export default function SalesInvoices() {
    const toast = useToast();
    const business = useSelector((state: any) => state.selections.value?.business);
    const [isAddFileModalOpen, setIsAddFileModalOpen] = useState(false);
    const [selectedTab, setSelectedTab] = useState<string>("Uploaded");
    const [selectedDocuments, setSelectedDocuments] = useState<Array<any>>([]);
    const [paginationQuery, setPaginationQuery] = useState({ page: 1, pageSize: 10 });
    const [pagination, setPagination] = useState<PaginationType>({ currentPage: 0, totalPages: 0, totalRecords: 0 });
    const [documents, setDocuments] = useState<Array<any>>([]);
    const [loading, setLoading] = useState(false);
    const [, setShowFilters] = useState(false);

    const reportRows = (() => {
        if (selectedTab !== 'Reports') return [];
        const map: Record<string, { gross: number; vat: number; net: number; count: number }> = {};
        documents.forEach((d) => {
            if (!d?.invoiceDate) return;
            const dt = new Date(d.invoiceDate);
            if (Number.isNaN(dt.getTime())) return;
            const key = `${dt.getFullYear()}-${(dt.getMonth() + 1).toString().padStart(2, '0')}`;
            if (!map[key]) map[key] = { gross: 0, vat: 0, net: 0, count: 0 };
            map[key].gross += Number(d.grossAmount || 0);
            map[key].vat += Number(d.vatAmount || 0);
            map[key].net += Number(d.netAmount || 0);
            map[key].count += 1;
        });
        return Object.entries(map).sort(([a], [b]) => (a < b ? 1 : -1)).map(([month, totals]) => ({ month, ...totals }));
    })();

    const handleBulkDelete = async () => {
        if (selectedDocuments.length === 0) {
            toast({ title: 'No document selected', status: 'warning', duration: 1200, position: 'top-right' });
            return;
        }
        if (!window.confirm(`Delete ${selectedDocuments.length} document(s)?`)) return;
        try {
            await PUT('/api/Documents/bulkDelete', { documentIds: selectedDocuments });
            toast({ title: 'Deleted', status: 'success', duration: 1200, position: 'top-right' });
            setSelectedDocuments([]);
            FetchList();
        } catch {
            toast({ title: 'Delete failed', status: 'error', duration: 1500, position: 'top-right' });
        }
    };
    const [editModalProps, setEditModalProps] = useState<EditModalProps>({
        selectedDocument: null,
        documents,
        isOpen: false,
        onClose: onEditModalClose
    });

    const convertDate = (date: string) => {
        const datehistory = new Date(date).toUTCString()
        return datehistory;
    }

    const onEditModalOpen = async (selectedDocument: any) => {
        if(selectedDocument !== null){
            if(selectedDocument.status === "Data Entered"){
                setEditModalProps({ ...editModalProps, selectedDocument, isOpen: true })
            }
        }
    }

    function onEditModalClose() {
        setEditModalProps({ ...editModalProps, isOpen: false })
    }

    useEffect(() => {
        FetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [business, selectedTab, paginationQuery]);

    useEffect(() => {
        setEditModalProps({ ...editModalProps, documents });
        // console.log(documents);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [documents]);

    async function FetchList() {
        setLoading(true);
        try {
            if (business) {
                const res = await GET(`/api/Documents/${business?.id}/SalesInvoice/list?page=${paginationQuery.page}&pageSize=${paginationQuery.pageSize}&status=${selectedTab}&sortColumn=Date&sortOrder=desc`);
                setPagination(res.data.pagination);
                setDocuments(res.data.documents);
                toast({
                    title: 'Data Fetched',
                    description: 'Data has been fetched successfully!',
                    status: 'success',
                    isClosable: false,
                    duration: 1000,
                    position: 'top-right'
                });
            }
        } catch (err) {
            toast({
                title: 'Something Went Wrong!',
                description: 'An unexpected error occured while proceeding your request!',
                status: 'error',
                isClosable: false,
                duration: 1000,
                position: 'top-right'
            });
        }
        setLoading(false);
    }

    const handleSelectedDocument = (e: any) => {
        if (e.target.checked) {
            setSelectedDocuments([...selectedDocuments, e.target.id]);
        } else {
            setSelectedDocuments(selectedDocuments.filter((doc) => doc !== e.target.id));
        }
    }

    const DownloadFile = async () => {
        if(selectedDocuments.length === 0 || selectedDocuments.length === undefined){
            toast({
                title: 'No Document Selected!',
                description: 'Please select a document to download!',
                status: 'error',
                isClosable: false,
                duration: 1000,
                position: 'top-right'
            });
            return;
        }
        const response = await GET(`/api/Documents/exportDocument?doc=${selectedDocuments[0]}`);
        const url = window.URL.createObjectURL(new Blob([response.data], {type:'data:application/vnd.ms-excel;base64'}));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', selectedDocuments[0]);
        document.body.appendChild(link);
        link.click();
    }

    const handleApproveDocument = async (docno: any) => {
            var arr: any[] = [docno];
            const arr_to_approve = {'documents_ids': arr, 'status': "Exported"};
            try {
                const res = await PUT(`/api/Documents/exported`, arr_to_approve);
                if (res.status === 'success') {
                    toast({
                        title: 'Document Approved',
                        description: 'Document have been approved successfully!',
                        status: 'success',
                        isClosable: false,
                        duration: 1000,
                        position: 'top-right'
                    });
                    FetchList();
                }
            } catch (err) {
                toast({
                    title: 'Something Went Wrong!',
                    description: 'An unexpected error occured while proceeding your request!',
                    status: 'error',
                    isClosable: false,
                    duration: 1000,
                    position: 'top-right'
                });
            }
    }

    

    function onAddFileModalClose() {
        setIsAddFileModalOpen(false);
    }

    return (
        <Stack w={'full'} justify={"center"} align={"center"} spacing={6}>
            <Stack
                direction={{ base: 'column', md: 'column', lg: 'column', xl: 'row' }}
                justify={'space-between'}
                boxShadow={'base'}
                rounded={'xl'}
                w={'98%'}
                p={4}
                spacing={{ base: 6, md: 6, lg: 6, xl: 0 }}
            >
                <Stack
                    align={'flex-start'}
                    justify={'start'}
                    spacing={4}
                >
                    <Stack direction={'row'}>
                        <Icon
                            as={FaFileInvoiceDollar}
                            fontSize={'5xl'}
                            color={'primary.400'}
                        />
                        <Stack spacing={1}>
                            <Heading fontSize={'xl'}>Sale Invoices</Heading>
                            <Text fontSize={'sm'}>All sales invoices & orders</Text>
                        </Stack>
                    </Stack>
                    <Stack
                        direction={'row'}
                    >
                        <TabButton
                            active={selectedTab === ""}
                            onClick={() => setSelectedTab("")}
                        >
                            All
                        </TabButton>
                        <TabButton
                            active={selectedTab === "Uploaded"}
                            onClick={() => setSelectedTab("Uploaded")}
                        >
                            New
                        </TabButton>
                        <TabButton
                            active={selectedTab === "Data Entered"}
                            onClick={() => setSelectedTab("Data Entered")}
                        >
                            Processed
                        </TabButton>
                        <TabButton
                            active={selectedTab === "Exported"}
                            onClick={() => setSelectedTab("Exported")}
                        >
                            Approved
                        </TabButton>
                        <TabButton
                            active={selectedTab === "Reports"}
                            onClick={() => setSelectedTab("Reports")}
                        >
                            Reports
                        </TabButton>
                    </Stack>
                </Stack>
                <Stack align={{ lg: 'flex-start', xl: 'flex-end' }} justify={'end'} spacing={4}>
                    <Stack direction={'row'}>
                        <Button
                            leftIcon={<BiCheck />}
                            size={'sm'}
                            color={'white'}
                            bg={'primary.400'}
                            _hover={{ bg: 'primary.400' }}
                            _active={{ bg: 'primary.400' }}
                        >
                            Approve
                        </Button>
                        <Button
                            leftIcon={<FaEdit />}
                            size={'sm'}
                            color={'white'}
                            bg={'primary.400'}
                            _hover={{ bg: 'primary.400' }}
                            _active={{ bg: 'primary.400' }}
                        >
                            Bulk Edit
                        </Button>
                        <Button
                            leftIcon={<FaColumns />}
                            size={'sm'}
                            color={'white'}
                            bg={'primary.400'}
                            _hover={{ bg: 'primary.400' }}
                            _active={{ bg: 'primary.400' }}
                        >
                            Columns
                        </Button>
                        <Button
                            leftIcon={<BiTransfer />}
                            size={'sm'}
                            color={'white'}
                            bg={'primary.400'}
                            _hover={{ bg: 'primary.400' }}
                            _active={{ bg: 'primary.400' }}
                        >
                            Transfer
                        </Button>
                    </Stack>
                    <Stack direction={'row'}>
                        <Button
                            leftIcon={<FaPlus />}
                            size={'sm'}
                            color={'white'}
                            bg={'primary.400'}
                            _hover={{ bg: 'primary.400' }}
                            _active={{ bg: 'primary.400' }}
                            onClick={() => setIsAddFileModalOpen(true)}
                        >
                            Add Files
                        </Button>
                        <Button
                            leftIcon={<FaDownload />}
                            size={'sm'}
                            color={'white'}
                            bg={'primary.400'}
                            _hover={{ bg: 'primary.400' }}
                            _active={{ bg: 'primary.400' }}
                            onClick={() => DownloadFile()}
                        >
                            Export CSV
                        </Button>
                        <Button
                            leftIcon={<FaSync />}
                            size={'sm'}
                            color={'white'}
                            bg={'primary.400'}
                            _hover={{ bg: 'primary.400' }}
                            _active={{ bg: 'primary.400' }}
                            onClick={() => FetchList()}
                        >
                            Refresh
                        </Button>
                        <Button
                            leftIcon={<FaFilter />}
                            size={'sm'}
                            color={'white'}
                            bg={'primary.400'}
                            _hover={{ bg: 'primary.400' }}
                            _active={{ bg: 'primary.400' }}
                            onClick={() => setShowFilters((v) => !v)}
                        >
                            Filter
                        </Button>
                        <Button
                            leftIcon={<FaTrash />}
                            size={'sm'}
                            color={'white'}
                            bg={'primary.400'}
                            _hover={{ bg: 'primary.400' }}
                            _active={{ bg: 'primary.400' }}
                            onClick={handleBulkDelete}
                        >
                            Delete
                        </Button>
                    </Stack>
                </Stack>
            </Stack>

            <Stack boxShadow={'base'} rounded={'xl'} w={'98%'} p={4} spacing={4}>
                {selectedTab === 'Reports' ? (
                    <TableContainer h={'sm'} overflowY={'scroll'}>
                        <Table size={'sm'}>
                            <Thead position={'sticky'} top={0} zIndex={1} bg={'primary.400'}>
                                <Tr>
                                    <Th color={'white'}>Month</Th>
                                    <Th color={'white'} isNumeric>Invoices</Th>
                                    <Th color={'white'} isNumeric>Gross Total</Th>
                                    <Th color={'white'} isNumeric>VAT Total</Th>
                                    <Th color={'white'} isNumeric>Net Total</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {reportRows.length === 0 ? (
                                    <Tr><Td colSpan={5} textAlign={'center'}>No data for this period</Td></Tr>
                                ) : reportRows.map((r) => (
                                    <Tr key={r.month}>
                                        <Td>{r.month}</Td>
                                        <Td isNumeric>{r.count}</Td>
                                        <Td isNumeric>{r.gross.toFixed(2)}</Td>
                                        <Td isNumeric>{r.vat.toFixed(2)}</Td>
                                        <Td isNumeric>{r.net.toFixed(2)}</Td>
                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>
                    </TableContainer>
                ) : (
                <TableContainer h={'sm'} overflowY={'scroll'}>
                    <Table size='sm'>
                        <Thead position={'sticky'} top={'0%'} zIndex={1} h={'50px'} bg={'primary.400'} alignItems={'flex-start'}>
                            <Tr>
                                <Th
                                    color={'white'}
                                    isNumeric
                                >
                                    Doc ID
                                </Th>
                                <Th
                                    color={'white'}
                                >
                                    Doc Type
                                </Th>
                                <Th
                                    color={'white'}
                                >
                                    Customer Name
                                </Th>
                                <Th
                                    color={'white'}
                                >
                                    Invoice Date
                                </Th>
                                <Th
                                    color={'white'}
                                >
                                    <Text fontSize={'sm'} fontWeight={'bold'}>Amount</Text>
                                    <Text fontSize={'sm'} fontWeight={'light'}>Inv. Currency</Text>
                                </Th>
                                <Th
                                    color={'white'}
                                >
                                    <Text fontSize={'sm'} fontWeight={'bold'}>Amount</Text>
                                    <Text fontSize={'sm'} fontWeight={'light'}>Base Currency</Text>
                                </Th>
                                <Th
                                    color={'white'}
                                >
                                    Category
                                </Th>
                                <Th
                                    color={'white'}
                                >
                                    Payment Method
                                </Th>
                                <Th
                                    color={'white'}
                                >
                                    Status
                                </Th>
                                <Th
                                    color={'white'}
                                    colSpan={2}
                                    textAlign={'center'}
                                >
                                    Actions
                                </Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {
                                documents.length > 0 ? (
                                    documents.map((doc, idx) => (
                                        <Tr key={idx}>
                                            <Td display={'flex'} justifyContent={'space-between'}><Checkbox colorScheme="primary" id={doc?.invoiceNumber} onChange={handleSelectedDocument} />{doc?.invoiceNumber}</Td>
                                            <Td>{doc?.documentType}</Td>
                                            <Td>{doc?.contact?.name}</Td>
                                            <Td>{doc?.invoiceDate !== null && convertDate(doc?.invoiceDate)}</Td>
                                            <Td>{doc?.grossAmount}</Td>
                                            <Td>{doc?.convertedGrossAmount}</Td>
                                            <Td>{doc?.category?.category}</Td>
                                            <Td>{doc?.paymentMethod?.bankName}</Td>
                                            <Td>{<ProcessingText status={doc?.status} />}</Td>
                                            {doc?.status === "Data Entered" ?
                                                    <Td as={Stack} spacing={4} direction={'row'}>
                                                        <Button
                                                            size={'sm'}
                                                            leftIcon={<FaEdit />}
                                                            color={'white'}
                                                            bg={'primary.400'}
                                                            _hover={{ bg: 'primary.400' }}
                                                            _active={{ bg: 'primary.400' }}
                                                            onClick={() => handleApproveDocument(doc?.invoiceNumber)}
                                                        >
                                                            Approve
                                                        </Button> 
                                                        <IconButton
                                                        aria-label="Document"
                                                        icon={<BiFile />}
                                                        color={'green.400'}
                                                        bg={'transparent'}
                                                        fontSize={'xl'}
                                                        rounded={'full'}
                                                        onClick={() => onEditModalOpen(doc)}
                                                    />
                                                    </Td>
                                                    :
                                                    <Td>
                                                        {'No Actions'}
                                                    </Td>
                                                    }
                                                    
                                        </Tr>
                                    ))

                                )
                                    :
                                    (
                                        loading ?
                                            (
                                                <Tr>
                                                    <Td colSpan={9}>
                                                        <Flex gap={2} justify={'center'} align={'center'}>
                                                            <Text>Loading</Text>
                                                            <Spinner size={'sm'} color={'primary.400'} />
                                                        </Flex>
                                                    </Td>
                                                </Tr>
                                            )
                                            :
                                            (
                                                <Tr>
                                                    <Td textAlign={'center'} colSpan={9}>No Data Available</Td>
                                                </Tr>
                                            )
                                    )
                            }
                        </Tbody>
                    </Table>
                </TableContainer>
                )}
                <Stack
                    direction={'row'}
                    justify={"space-between"}
                    align={'center'}
                >
                    <Text>Showing {documents?.length} of {pagination.totalRecords} results</Text>
                    <Stack direction={'row'} spacing={4}>
                        <Select
                            defaultValue={5}
                            onChange={(e) => {
                                setPaginationQuery({ page: 1, pageSize: parseInt(e.target.value) });
                            }}>
                            <option value={5}>5 Per Page</option>
                            <option value={10}>10 Per Page</option>
                            <option value={25}>25 Per Page</option>
                            <option value={50}>50 Per Page</option>
                        </Select>
                        <Stack direction={'row'}>
                            <IconButton
                                aria-label="Previous"
                                icon={<FaArrowLeft />}
                                rounded={'full'}
                                disabled={pagination.currentPage <= 1}
                                onClick={() => setPaginationQuery({ ...paginationQuery, page: pagination.currentPage - 1 })}
                            />
                            <IconButton
                                aria-label="Previous"
                                icon={<FaArrowRight />}
                                rounded={'full'}
                                disabled={pagination.currentPage >= pagination.totalPages}
                                onClick={() => setPaginationQuery({ ...paginationQuery, page: pagination.currentPage + 1 })}
                            />
                        </Stack>
                    </Stack>
                </Stack>
            </Stack>
            <EditModal {...editModalProps} Refresh={FetchList} />
            <AddDocument isOpen={isAddFileModalOpen} onClose={onAddFileModalClose} DocType={"Sales Invoice"} RefreshList={FetchList} />
        </Stack >
    )
}